import type { TrainingLevel } from "../data/securityModules";

export type Lang = "de" | "en";

/** Zweisprachiger Text – Deutsch ist Pflicht, Englisch optional (Fallback auf DE). */
export type Loc = { de: string; en: string };

export type ScenarioOption = { text: Loc; correct: boolean; feedback: Loc };
export type KeypointItem = { title: Loc; detail: Loc };
export type ChecklistItem = { label: Loc; detail: Loc };
export type ComparisonSide = { label: Loc; items: Loc[] };
export type StatItem = { value: Loc; label: Loc };

/** Wie ein PDF-Block dem Mitarbeiter angezeigt wird. Default: "both". */
export type PdfDisplay = "embed" | "newtab" | "both";

/** Wie ein Link-Block angezeigt wird. Default: "both" (Rahmen + Fenster-Button). */
export type LinkDisplay = "embed" | "newtab" | "both";

/** Höhe des eingebetteten Rahmens. Default: "tall". */
export type LinkHeight = "medium" | "tall";

/** Wo das optionale Bild eines Textabsatzes steht. Default: "below". */
export type TextImagePosition = "above" | "below" | "left" | "right";

export type ContentBlock =
  /** image = Storage-Pfad im Modul-Asset-Bucket (wie bei Quizfragen). */
  | { type: "text"; body: Loc; image?: string; imageAlt?: Loc; imagePosition?: TextImagePosition }
  | { type: "heading" | "tip" | "warning"; body: Loc }
  | { type: "image" | "video"; url: string; alt?: Loc }
  | { type: "pdf"; path: string; filename: string; pathEn?: string; filenameEn?: string; title?: Loc; display?: PdfDisplay }
  | { type: "link"; url: string; title?: Loc; note?: Loc; display?: LinkDisplay; height?: LinkHeight }
  | { type: "scenario"; situation: Loc; options: ScenarioOption[] }
  | { type: "keypoints"; intro: Loc; items: KeypointItem[] }
  | { type: "checklist"; intro: Loc; items: ChecklistItem[] }
  | { type: "comparison"; intro: Loc; left: ComparisonSide; right: ComparisonSide }
  | { type: "stats"; items: StatItem[] };

export type Question = {
  question: Loc;
  /** Optionales Bild zur Frage (Storage-Pfad im Modul-Asset-Bucket). */
  image?: string;
  imageAlt?: Loc;
  options: Loc[];
  /** Optionale Bilder je Antwort, index-gleich mit options ("" = kein Bild). */
  optionImages?: string[];
  /** Erste richtige Antwort. Bleibt gefüllt, damit ältere Daten/Clients weiter funktionieren. */
  correctIndex: number;
  /** Alle richtigen Antworten. Fehlt = nur correctIndex zählt. */
  correctIndices?: number[];
  explanation?: Loc;
};

export type CustomModule = {
  id: string;
  title: Loc;
  shortTitle: Loc;
  description: Loc;
  icon: string;
  color: string;
  duration: Loc;
  level: TrainingLevel;
  topicId: string;
  content: ContentBlock[];
  questions: Question[];
  /** Wie viele Fragen pro Versuch aus dem Pool gezogen werden. Fehlt = QUIZ_TARGET_LENGTH. */
  questionCount?: number;
  /** Bestehensgrenze in Prozent (0-100). Fehlt = globaler Tenant-Default. */
  passThreshold?: number;
  /** Antwortoptionen pro Versuch zufällig anordnen. Fehlt = an. */
  shuffleOptions?: boolean;
  /** Video-Bausteine müssen vollständig angesehen werden (components/ForcedVideo.tsx). Fehlt = aus. */
  forceVideo?: boolean;
  /** Laufende Versionsnummer, vom Server gesetzt. Fehlt bei Modulen, die seit
   *  Einführung des Verlaufs noch nicht gespeichert wurden. Siehe utils/moduleHistory.ts. */
  version?: number;
  createdAt: string;
  updatedAt: string;
};

/** Fallback-Bestehensgrenze in Prozent, wenn weder Modul noch Tenant etwas gesetzt haben. */
export const DEFAULT_PASS_THRESHOLD = 80;

/** Effektive Bestehensgrenze in Prozent: Modul-Wert vor Tenant-Default vor Hard-Default. */
export function effectivePassThreshold(module: Pick<CustomModule, "passThreshold">, tenantDefault?: number): number {
  const candidates = [module.passThreshold, tenantDefault, DEFAULT_PASS_THRESHOLD];
  for (const c of candidates) {
    if (typeof c === "number" && Number.isFinite(c) && c > 0 && c <= 100) return Math.round(c);
  }
  return DEFAULT_PASS_THRESHOLD;
}

/**
 * Normalisiert eine vom Kunden-Admin eingetragene URL für <iframe src> / <a href>.
 * SICHERHEITSGRENZE: Ohne diese Prüfung liefe eine `javascript:`-URL beim Anzeigen
 * des Moduls als Skript im Origin der Schulungsplattform – ein Kunden-Admin könnte
 * damit Sessions seiner Mitarbeiter abgreifen. Erlaubt sind nur http/https (http wird auf https gehoben). Gibt null zurück,
 * wenn die URL nicht taugt.
 */
export function safeEmbedUrl(raw: string): string | null {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  // http wird auf https gehoben – die CSP (upgrade-insecure-requests) tut das ohnehin,
  // so verschwindet kein Link, der vorher schon funktioniert hat.
  if (parsed.protocol === "http:") parsed.protocol = "https:";
  if (parsed.protocol !== "https:") return null;
  return parsed.href;
}

export const emptyLoc = (): Loc => ({ de: "", en: "" });

/** Alle richtigen Antwort-Indizes einer Frage. Ohne correctIndices zählt nur correctIndex. */
export function correctIndexSet(q: Question): Set<number> {
  const raw = Array.isArray(q.correctIndices) && q.correctIndices.length ? q.correctIndices : [q.correctIndex];
  const valid = raw.filter(i => Number.isInteger(i) && i >= 0 && i < q.options.length);
  return new Set(valid.length ? valid : [0]);
}

/** Setzt die richtigen Antworten einer Frage und hält correctIndex als ersten Treffer nach. */
export function withCorrectIndices(q: Question, indices: number[]): Question {
  const sorted = [...new Set(indices)].filter(i => Number.isInteger(i) && i >= 0 && i < q.options.length).sort((a, b) => a - b);
  const next = sorted.length ? sorted : [0];
  return { ...q, correctIndices: next, correctIndex: next[0] };
}

/** Zufällige Anzeigereihenfolge (Fisher-Yates) – liefert die Original-Indizes der Optionen. */
export function shuffledOptionOrder(count: number): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

/**
 * Stabiler Schlüssel einer Frage für das adaptive Quiz-Gedächtnis. Basiert auf dem
 * deutschen Fragetext (normalisiert), damit er Umsortieren übersteht. Wird der Text
 * bearbeitet, gilt die Frage als neu – gewollt.
 */
export function questionKey(q: Question): string {
  const base = (q.question.de || q.question.en || "").trim().toLowerCase().replace(/\s+/g, " ");
  let h = 5381;
  for (let i = 0; i < base.length; i++) h = (((h << 5) + h) ^ base.charCodeAt(i)) | 0;
  return "q" + (h >>> 0).toString(36);
}

/** Wählt für einen PDF-Block die zur Sprache passende Datei. EN fällt auf DE zurück. */
export function pickPdf(
  block: { path: string; filename: string; pathEn?: string; filenameEn?: string },
  lang: Lang,
): { path: string; filename: string } {
  if (lang === "en" && block.pathEn) return { path: block.pathEn, filename: block.filenameEn || block.filename };
  return { path: block.path, filename: block.filename };
}

/** Liefert den Text in der gewünschten Sprache, mit Fallback auf die jeweils andere. */
export function pick(value: Loc | string | undefined | null, lang: Lang): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (lang === "en") return value.en || value.de || "";
  return value.de || value.en || "";
}

// Was ein Speichern eigener Module ändert, gemessen am geladenen Stand: der
// Server übernimmt nur diese Module und meldet 409, wenn jemand anderes eines
// davon inzwischen gespeichert hat (mergeOntoStored in api/phishing-campaigns.js).
export function customModuleBase(
  loaded: ReadonlyArray<{ id: string; version?: number }>,
  next: ReadonlyArray<{ id: string }>,
): { versions: Record<string, number>; changed: string[]; deleted: string[] } {
  const loadedById = new Map(loaded.map(m => [m.id, m]));
  const nextIds = new Set(next.map(m => m.id));
  return {
    versions: Object.fromEntries(loaded.map(m => [m.id, typeof m.version === "number" ? m.version : 0])),
    changed: next.filter(m => JSON.stringify(m) !== JSON.stringify(loadedById.get(m.id))).map(m => m.id),
    deleted: loaded.filter(m => !nextIds.has(m.id)).map(m => m.id),
  };
}
